/**
 * Sources Section Component
 *
 * Displays a collapsible list of documents cited in a message,
 * grouped by document with citation counts.
 * Follows RAGFlow's approach of showing only cited documents.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { useState } from 'react';
import { SourceDocumentDialog } from './SourceDocumentDialog';
export function SourcesSection({ documents }) {
    const [isExpanded, setIsExpanded] = useState(true);
    const [selectedSource, setSelectedSource] = useState(null);
    if (documents.length === 0) return null;
    const documentCount = documents.length;
    const documentLabel = documentCount === 1 ? 'document' : 'documents';
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-6 border-t border-gray-200 dark:border-gray-700 pt-4",
        children: [
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                onClick: ()=>setIsExpanded(!isExpanded),
                className: "flex items-center gap-2 w-full text-left text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors",
                "aria-label": isExpanded ? 'Collapse sources' : 'Expand sources',
                "aria-expanded": isExpanded,
                children: [
                    isExpanded ? /*#__PURE__*/ _jsx(ChevronUp, {
                        size: 16
                    }) : /*#__PURE__*/ _jsx(ChevronDown, {
                        size: 16
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        children: [
                            "Sources (",
                            documentCount,
                            " ",
                            documentLabel,
                            ")"
                        ]
                    })
                ]
            }),
            isExpanded && /*#__PURE__*/ _jsx("div", {
                className: "mt-3 space-y-2",
                children: documents.map((doc, idx)=>/*#__PURE__*/ _jsxs("div", {
                        className: `flex items-start gap-3 p-3 rounded-lg
                         bg-gray-50 dark:bg-gray-800/50
                         border border-gray-200 dark:border-gray-700`,
                        children: [
                            /*#__PURE__*/ _jsx(FileText, {
                                className: "text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5",
                                size: 18
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex-1 min-w-0",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-baseline gap-2 mb-1",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "link",
                                                size: "inline",
                                                disabled: !doc.documentId,
                                                onClick: ()=>setSelectedSource(doc),
                                                className: "text-sm font-medium text-foreground truncate underline",
                                                children: doc.name
                                            }),
                                            doc.type && /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs text-gray-500 dark:text-gray-400 uppercase flex-shrink-0",
                                                children: doc.type
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400",
                                        children: [
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    doc.count,
                                                    " ",
                                                    doc.count === 1 ? 'citation' : 'citations'
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                children: "•"
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "text-blue-600 dark:text-blue-400",
                                                children: [
                                                    "[📄 ",
                                                    doc.citations.join(', '),
                                                    "]"
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }, doc.documentId || idx))
            }),
            selectedSource?.documentId ? /*#__PURE__*/ _jsx(SourceDocumentDialog, {
                documentId: selectedSource.documentId,
                name: selectedSource.name,
                onClose: ()=>setSelectedSource(null)
            }) : null
        ]
    });
}
